import { ContractProduct } from "shared/types/rental-contracts";
import { findActivePrice, findBasePrice } from "shared/utils/priceHistory";

/**
 * Derive the base rent (basishuurprijs) from a contract product.
 *
 * The base price is the OLDEST price entry: sorted ascending by effective date
 * (null dates — the initial entry usually lacks one — sort first), then by id.
 * Mirrors the logic used by EditBasePriceModal so the optimistic row update on
 * the indexaties page matches what the backend will report on the next reload.
 */
export const deriveBasePrice = (product: ContractProduct | undefined | null): number =>
  findBasePrice(product?.price)?.amount ?? 0;

/**
 * Derive the current rent (huidige huurprijs) from a contract product.
 *
 * Among the prices whose effective date has arrived (future-dated changes don't
 * count yet), the active price is the row that was **recorded last** — the
 * highest id. This deliberately mirrors `extractActivePrice` in the backend
 * (`yallyhub-be/src/shared/helpers/helpers.service.ts`), which fills
 * `activePrice` / `activeProvision` on the contract, drives the Verhuur-overzicht
 * and is what gets pushed to Odoo.
 *
 * ⚠️ Do NOT sort by effective date here. That was the old rule and it made the
 * indexatiemail disagree with the rest of YallyHub: a price row entered with a
 * mistyped year (price id 2585 on unit 2400-COLL-18-7 has effectiveDate 0026-01-27
 * instead of 2026-01-27) sorts as the *oldest* row, so the mail fell back to the
 * € 55 opening provision while YallyHub and Odoo correctly showed the € 20 that
 * replaced it.
 *
 * Should this rule ever need to change, change it in `helpers.service.ts` first
 * and mirror it here — one rule on both sides, otherwise the numbers drift apart
 * again.
 */
export const deriveCurrentPrice = (product: ContractProduct | undefined | null): number =>
  findActivePrice(product?.price)?.amount ?? 0;
